<template>
  <Transition name="scrape-bar">
    <div
      v-if="progress"
      class="scrape-status-bar"
      :class="{
        'scrape-status-bar--complete': progress.complete,
        'scrape-status-bar--failed': progress.failed,
      }"
      role="status"
      aria-live="polite"
    >
      <div class="scrape-status-bar__inner">
        <!-- Icon -->
        <v-progress-circular
          v-if="!progress.complete && !progress.failed"
          indeterminate
          size="14"
          width="2"
          color="primary"
          class="scrape-status-bar__spinner flex-shrink-0"
        />
        <v-icon
          v-else-if="progress.complete"
          size="16"
          color="success"
          class="flex-shrink-0"
        >
          mdi-check-circle
        </v-icon>
        <v-icon
          v-else
          size="16"
          color="warning"
          class="flex-shrink-0"
        >
          mdi-alert-circle-outline
        </v-icon>

        <!-- Text -->
        <span class="text-caption scrape-status-bar__text">{{ statusText }}</span>

        <!-- Step counter (running only) -->
        <span
          v-if="!progress.complete && !progress.failed && stepCount > 0"
          class="text-caption text-disabled scrape-status-bar__counter flex-shrink-0"
        >
          {{ activeStep + 1 }}&thinsp;/&thinsp;{{ stepCount }}
        </span>

        <!-- Dismiss (failed only) -->
        <v-btn
          v-if="progress.failed"
          variant="text"
          size="x-small"
          icon="mdi-close"
          class="flex-shrink-0"
          :aria-label="t('labels.close')"
          @click="$emit('dismiss')"
        />
      </div>

      <!-- Progress line -->
      <div
        v-if="!progress.complete && !progress.failed"
        class="scrape-status-bar__track"
      >
        <div
          class="scrape-status-bar__fill"
          :style="{ width: fillWidth }"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ScrapeProgressView } from '@/lib/ai/scrapeProgress'

const props = defineProps<{
  progress: ScrapeProgressView | null
}>()

defineEmits<{ dismiss: [] }>()

const { t } = useI18n()

const activeStep = computed(() => props.progress?.activeStep ?? 0)
const stepCount = computed(() => props.progress?.steps.length ?? 0)

const fillWidth = computed(() => {
  if (!props.progress || stepCount.value === 0) return '0%'
  const pct = Math.round(((activeStep.value + 1) / stepCount.value) * 100)
  return `${pct}%`
})

const statusText = computed(() => {
  if (!props.progress) return ''
  if (props.progress.complete) {
    const key = 'onboarding.wizard.scrape-progress.success.headline'
    const tr = t(key)
    return tr !== key ? tr : props.progress.headline
  }
  if (props.progress.failed) {
    const key = 'onboarding.wizard.scrape-progress.failed.headline'
    const tr = t(key)
    return tr !== key ? tr : props.progress.headline
  }
  const stepId = props.progress.steps[activeStep.value]?.id ?? 'connect'
  const key = `onboarding.wizard.scrape-progress.live.${stepId}.headline`
  const tr = t(key)
  return tr !== key ? tr : props.progress.headline
})
</script>

<style scoped>
.scrape-status-bar {
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary), 0.12) 0%,
    rgba(var(--v-theme-primary), 0.06) 100%
  );
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.25);
  overflow: hidden;
}

.scrape-status-bar--complete {
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-success), 0.12) 0%,
    rgba(var(--v-theme-success), 0.06) 100%
  );
  border-bottom-color: rgba(var(--v-theme-success), 0.3);
}

.scrape-status-bar--failed {
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-warning), 0.12) 0%,
    rgba(var(--v-theme-warning), 0.06) 100%
  );
  border-bottom-color: rgba(var(--v-theme-warning), 0.3);
}

.scrape-status-bar__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  min-height: 40px;
}

.scrape-status-bar__text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: rgb(var(--v-theme-on-surface));
}

.scrape-status-bar__counter {
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: rgba(var(--v-theme-primary), 0.85);
  font-weight: 600;
}

.scrape-status-bar__track {
  height: 3px;
  background: rgba(var(--v-theme-primary), 0.15);
}

.scrape-status-bar__fill {
  height: 100%;
  background: linear-gradient(
    90deg,
    rgb(var(--v-theme-primary)),
    rgba(var(--v-theme-primary), 0.7)
  );
  transition: width 0.6s ease;
  box-shadow: 0 0 6px rgba(var(--v-theme-primary), 0.5);
}

/* Enter / leave transitions */
.scrape-bar-enter-active,
.scrape-bar-leave-active {
  transition:
    max-height 0.25s ease,
    opacity 0.25s ease;
  max-height: 56px;
}

.scrape-bar-enter-from,
.scrape-bar-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
